import type { Rarity } from '@/domain/content.types';
import { useT } from '@/i18n/useT';

/**
 * Colour is a hint, never the information: the badge always carries the actual
 * drop chance as text, so it survives colour-blindness and greyscale printing.
 */
const TONE: Record<Rarity, string> = {
  guaranteed: 'text-pip border-pip-faint bg-pip-faint/25',
  common: 'text-text-dim border-line bg-surface-2',
  rare: 'text-violet border-violet/25 bg-violet/10',
  legendary: 'text-amber border-amber/30 bg-amber/10',
  'ultra-rare': 'text-danger border-danger/30 bg-danger/10',
  unknown: 'text-text-faint border-line bg-surface-2',
};

interface RarityBadgeProps {
  rarity?: Rarity;
  dropChance?: string;
}

export function RarityBadge({ rarity = 'unknown', dropChance }: RarityBadgeProps) {
  const { t } = useT();
  const rarityLabel = t(`rarity.${rarity}` as const);
  // Prefer the precise figure; fall back to the bucket name when absent.
  const text = dropChance ?? rarityLabel;

  return (
    <span
      title={dropChance ? `${rarityLabel} — ${dropChance}` : rarityLabel}
      className={`tnum inline-block truncate rounded-xs border px-1.5 py-px text-[11px] leading-4 ${TONE[rarity]}`}
    >
      {text}
    </span>
  );
}
